import { Modal } from "antd";
import Draggable from "react-draggable";
import { useDispatch, useSelector } from "react-redux";

import * as commonAPI from "../../../services/commonServices";
import * as masterServices from "../../../services/masterServices";
import { updateCustomerList, updateLocationList, updateMasterFilters } from "../../../appRedux/reducers/Common";
import { CustomNotification } from "../../../util/CustomNotification";
import { ModuleAndIdMapping, Modules } from "../../../constants/DatabaseConstants/MasterConstant";
import useValue from "../../../hooks/useValue";
import { useReactDraggable } from "../../../hooks/useReactDraggable";
import LocalStorageUtil from "../../../util/LocalStorageUtil";
import { LocalStorageConstants } from "../../../constants/ApplicationConstants/LocalStorageConstants";

import type { RootState } from "../../../appRedux/store";
import type FixMeLater from "../../../shared/types/fixMeLater.type";
import { MasterFiltersConstant } from "../../../constants/ApplicationConstants/MasterConstant";

type DeleteModalProps = {
  rowId: number;
  moduleId: number;
  rowData: FixMeLater | null;
  handleEnableRefresh: () => void;
  handleClearRowIdAndActionType: () => void;
};

const getDeleteModalTitle = (moduleId: number, rowData: FixMeLater) => {
  let title = `Are you sure you want to ${rowData?.is_active ? "inactivate" : "activate"} this `;

  switch (moduleId) {
    case ModuleAndIdMapping[Modules.ROLE_MASTER]:
      title += "Role master record?";
      break;
    case ModuleAndIdMapping[Modules.CONFIG_MASTER]:
      title += "Config master record?";
      break;
    case ModuleAndIdMapping[Modules.USER_ROLE_MASTER]:
      title += "User Role allocation master record?";
      break;
    case ModuleAndIdMapping[Modules.CUSTOMER_MASTER]:
      title += "Customer master record?";
      break;
    case ModuleAndIdMapping[Modules.LOCATION_TYPE_MASTER]:
      title += "Location Type master record?";
      break;
    case ModuleAndIdMapping[Modules.VEHICLE_TYPE_MASTER]:
      title += "Vehicle Type master record?";
      break;
    case ModuleAndIdMapping[Modules.PARKING_MAP_TYPE_MASTER]:
      title += "Parking Map Type master record?";
      break;
    case ModuleAndIdMapping[Modules.CUSTOMER_LOCATION_MASTER]:
      title += "Customer Location master record?";
      break;
    case ModuleAndIdMapping[Modules.CUSTOMER_CLIENT_MASTER]:
      title += "Customer Client master record?";
      break;
    case ModuleAndIdMapping[Modules.KEY_FOB_MASTER]:
      title += "KeyFob master record?";
      break;
    case ModuleAndIdMapping[Modules.PRICING_MASTER]:
      title += "Pricing master record? ";
      break;
    case ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER]:
      title = `Are you sure you want to delete this KeyFob allocation master record? `;
      return (
        <div>
          {title}
          <p style={{ color: "red", marginTop: "1rem" }}>Ensure this record is not activated next time.</p>
        </div>
      );
    case ModuleAndIdMapping[Modules.FAQ_MASTER]:
      title += "FAQ master record?";
      break;
    case ModuleAndIdMapping[Modules.NOTIFICATION_MASTER]:
      title += "Notification master record?";
      break;
    case ModuleAndIdMapping[Modules.ADVANCE_BOOKING_QUOTA_MASTER]:
      return <div>Are you sure you want to delete this advance booking quota master record?</div>;
    default:
      break;
  }

  return title;
};

const DeleteModal = (props: DeleteModalProps) => {
  /* CONSTANTS */
  const dispatch = useDispatch();

  /* STATES */

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* CUSTOM HOOKS */
  const { value: showDeleteModal, handleDisableValue: handleDisableDeleteModal, handleEnableValue: handleEnableDeleteModal } = useValue(true);
  const { value: saving, handleEnableValue: handleEnableSaving, handleDisableValue: handleDisableSaving } = useValue(false);

  /* HOOK */
  const { bounds, onStart } = useReactDraggable();

  /* HANDLERS */

  /* get TopBar filters list */
  const getCustomerListAndUpdateState = async (recordId: number) => {
    try {
      /* getCustomerMasterList */
      const customerListResponse = await masterServices.getOneMasterListById(ModuleAndIdMapping[Modules.CUSTOMER_MASTER]);
      dispatch(updateCustomerList(customerListResponse?.["AllRecordsList"]));

      if (recordId === masterFilters?.customerId) {
        const customerRecordId = customerListResponse?.["AllRecordsList"]?.[0]?.id ?? null;

        const dataObj = {
          [MasterFiltersConstant.CUSTOMER_ID]: customerRecordId,
          [MasterFiltersConstant.LOCATION_ID]: null
        };

        LocalStorageUtil.localStorageUpdateData(LocalStorageConstants.MASTER_FILTERS, dataObj);
        dispatch(updateMasterFilters(dataObj));
      }
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  const getCustomerLocationListAndUpdateState = async (customerId: number | null | undefined, recordId: number) => {
    try {
      if (!customerId) return;
      /* getCustomerMasterList */
      const locationListResponse = await commonAPI.getLocationListByCustomerId(customerId);
      dispatch(updateLocationList(locationListResponse?.data));

      if (recordId === masterFilters?.locationId) {
        const dataObj = {
          [MasterFiltersConstant.CUSTOMER_ID]: customerId,
          [MasterFiltersConstant.LOCATION_ID]: null
        };

        LocalStorageUtil.localStorageUpdateData(LocalStorageConstants.MASTER_FILTERS, dataObj);
        dispatch(updateMasterFilters(dataObj));
      }
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  const handleCancel = () => {
    handleDisableDeleteModal();
    props.handleClearRowIdAndActionType();
  };

  const handleOk = async (rowData: FixMeLater) => {
    try {
      handleEnableSaving();
      if (props.rowId) await masterServices.deleteMasterAPI(props.moduleId, props.rowId);
      props.handleEnableRefresh();

      /* update topBar Customer list */
      if (props.moduleId === ModuleAndIdMapping[Modules.CUSTOMER_MASTER]) {
        await getCustomerListAndUpdateState(props.rowId);
      }

      /* update topBar CustomerLocation list */
      if (props.moduleId === ModuleAndIdMapping[Modules.CUSTOMER_LOCATION_MASTER]) {
        await getCustomerLocationListAndUpdateState(masterFilters?.customerId, props.rowId);
      }

      if (props.moduleId === ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER] || props.moduleId === ModuleAndIdMapping[Modules.ADVANCE_BOOKING_QUOTA_MASTER]) {
        CustomNotification.showSuccessMessage(`Record deleted successfully.`);
      } else {
        CustomNotification.showSuccessMessage(`Record ${rowData?.is_active ? "Inactivated" : "Activated"} Successfully.`);
      }
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableSaving();
      props.handleClearRowIdAndActionType();
      handleDisableDeleteModal();
    }
  };

  return (
    <Modal
      title={<div className="modal-container">{props.moduleId === ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER] || props.moduleId === ModuleAndIdMapping[Modules.ADVANCE_BOOKING_QUOTA_MASTER] ? "Delete Confirmation" : `Active/In-Active Confirmation`}</div>}
      open={showDeleteModal}
      onOk={() => handleOk(props.rowData)}
      onCancel={handleCancel}
      okText="Yes"
      cancelText="No"
      width="500px"
      destroyOnClose
      maskClosable={false}
      wrapClassName="delete-modal"
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
      okButtonProps={{
        disabled: saving,
        loading: saving
      }}
    >
      <h4>{getDeleteModalTitle(props.moduleId, props.rowData)}</h4>
    </Modal>
  );
};

export default DeleteModal;
